import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import StatusBadge from '@/components/ui/StatusBadge';
import Toggle from '@/components/ui/Toggle';
import { ApiError } from '@/lib/api';
import { siteScanApi, onboardingApi, type ListingRow, type ListingTypes, type ListingsPayload } from '@/lib/onboarding';
import { Field } from './fields';
import { areaCls, btnGhost, btnPrimary, fmtAed, fmtPct, inputCls, numOrNull } from './styles';

interface Props {
  data: ListingsPayload;
  types: ListingTypes | null;
  onRow: (row: ListingRow) => void;
  onReload: () => void;
  onToast: (message: string, tone?: 'success' | 'error') => void;
  canEdit: boolean;
}

type Channel = 'b2c' | 'b2b';
type SortKey = 'stock' | 'priceAsc' | 'priceDesc' | 'name';

const toneOf = (status: string) => (status === 'live' ? 'primary' : status === 'approved' ? 'secondary' : status === 'failed' ? 'danger' : status === 'archived' ? 'neutral' : 'accent');

/** Шаг 3: витрина в виде карточек сайта на реальных данных, карточка товара и панель правки (тексты, картинки, цены по каналам). */
export default function PreviewStep({ data, types, onRow, onReload, onToast, canEdit }: Props) {
  const { t } = useTranslation();
  const [supplier, setSupplier] = useState(false);
  const [channel, setChannel] = useState<Channel>('b2c');
  const [typeFilter, setTypeFilter] = useState('');
  const [sort, setSort] = useState<SortKey>('stock');
  const [open, setOpen] = useState<string | null>(null);
  const [tab, setTab] = useState<'cards' | 'prices'>('cards');
  const [busy, setBusy] = useState(false);

  const rows = useMemo(() => data.rows.filter((r) => r.listingStatus !== 'archived' && r.productStatus !== 'Discontinued'), [data.rows]);
  const priceOf = (r: ListingRow) => (channel === 'b2b' ? r.prices.b2b : r.prices.b2c) ?? 0;
  const list = useMemo(() => {
    const f = rows.filter((r) => !typeFilter || r.listing.specifications.en.Model === undefined || (r.listing.name.en || '').startsWith(typeFilter) || typeFilter === '');
    const byType = typeFilter ? f.filter((r) => (r.listing.name.en || '').startsWith(typeFilter)) : f;
    const sorters: Record<SortKey, (a: ListingRow, b: ListingRow) => number> = {
      stock: (a, b) => b.listing.availability.qty - a.listing.availability.qty,
      priceAsc: (a, b) => priceOf(a) - priceOf(b),
      priceDesc: (a, b) => priceOf(b) - priceOf(a),
      name: (a, b) => (a.listing.name.en || '').localeCompare(b.listing.name.en || ''),
    };
    return [...byType].sort(sorters[sort]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, typeFilter, sort, channel]);
  const typeCounts = useMemo(() => {
    const m = new Map<string, number>();
    rows.forEach((r) => {
      const ty = types?.types.find((x) => (r.listing.name.en || '').startsWith(x)) || '';
      if (ty) m.set(ty, (m.get(ty) || 0) + 1);
    });
    return [...m.entries()].sort((a, b) => b[1] - a[1]);
  }, [rows, types]);

  const current = open ? data.rows.find((r) => r.listing.sku === open) ?? null : null;

  const stockText = (r: ListingRow) => {
    const a = r.listing.availability;
    if (a.state === 'in_transit' && a.in_transit) return t('onb.inTransit', { count: a.in_transit.qty });
    if (a.qty > 0) return t('onb.inStock', { count: a.qty });
    return t('onb.onRequest');
  };

  const pullSite = async () => {
    setBusy(true);
    try {
      const r = await siteScanApi.apply(data.partner.id, {});
      onToast(r.applied > 0 ? t('pc.applied', { count: r.applied }) : t('pc.appliedNone'));
      onReload();
    } catch (e) {
      onToast(t('onb.error', { code: (e as ApiError).code }), 'error');
    } finally {
      setBusy(false);
    }
  };
  const recalc = async () => {
    setBusy(true);
    try {
      const r = await onboardingApi.recalc(data.partner.id);
      onToast(t('onb.recalcDone', { count: r.recalculated }));
      onReload();
    } catch (e) {
      onToast(t('onb.error', { code: (e as ApiError).code }), 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-background-200 bg-background-50 px-4 py-3">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex gap-1 rounded-md bg-background-100 p-0.5 text-[12px]">
            {(['cards', 'prices'] as const).map((k) => (
              <button key={k} type="button" onClick={() => setTab(k)} className={`cursor-pointer rounded px-3 py-1 font-medium ${tab === k ? 'bg-background-50 text-foreground-950 shadow-sm' : 'text-foreground-500'}`}>
                {t(k === 'cards' ? 'onb.tabCards' : 'onb.tabPrices')}
              </button>
            ))}
          </div>
          <label className="flex items-center gap-2 text-[12px] text-foreground-600">
            {t('onb.channel')}
            <select value={channel} onChange={(e) => setChannel(e.target.value as Channel)} className="h-8 rounded-md border border-background-200 bg-background-50 px-2 text-[12px]">
              <option value="b2c">{t('onb.channelB2c')}</option>
              <option value="b2b">{t('onb.channelB2b')}</option>
            </select>
          </label>
          <Toggle checked={supplier} onChange={setSupplier} label={t('onb.supplierMode')} />
        </div>
        <div className="flex items-center gap-3 text-[11.5px] text-foreground-500">
          <span>B2C +{data.summary.markupB2c}% · B2B +{data.summary.markupB2b}%</span>
          {canEdit && (
            <button type="button" onClick={recalc} disabled={busy} className={btnGhost}>
              <i className="ri-refresh-line text-[13px]" /> {t('onb.recalc')}
            </button>
          )}
          {canEdit && (
            <button type="button" onClick={pullSite} disabled={busy} className={btnGhost} title={t('pc.pullSiteHint')}>
              <i className="ri-download-2-line text-[13px]" /> {t('pc.pullSite')}
            </button>
          )}
        </div>
      </div>

      {tab === 'cards' && (
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-[200px_minmax(0,1fr)]">
          <aside className="text-[12.5px]">
            <p className="mb-2 text-[10.5px] font-semibold uppercase tracking-[0.12em] text-foreground-500">{t('onb.products', { count: rows.length })}</p>
            <ul className="divide-y divide-background-200 border-y border-background-200">
              <li>
                <button type="button" onClick={() => setTypeFilter('')} className={`flex w-full cursor-pointer justify-between py-1.5 ${!typeFilter ? 'font-semibold text-accent-700' : 'text-foreground-600'}`}>
                  {t('onb.filterAll')} <span className="text-foreground-400">{rows.length}</span>
                </button>
              </li>
              {typeCounts.map(([ty, n]) => (
                <li key={ty}>
                  <button type="button" onClick={() => setTypeFilter(ty)} className={`flex w-full cursor-pointer justify-between py-1.5 text-left ${typeFilter === ty ? 'font-semibold text-accent-700' : 'text-foreground-600'}`}>
                    {ty} <span className="text-foreground-400">{n}</span>
                  </button>
                </li>
              ))}
            </ul>
          </aside>
          <div>
            <div className="mb-2 flex items-center justify-end text-[11.5px] text-foreground-500">
              <select value={sort} onChange={(e) => setSort(e.target.value as SortKey)} className="h-8 rounded-md border border-background-200 bg-background-50 px-2 text-[12px]">
                {(['stock', 'priceAsc', 'priceDesc', 'name'] as const).map((k) => (
                  <option key={k} value={k}>
                    {t(`onb.sort.${k}`)}
                  </option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-px overflow-hidden rounded-lg border border-background-200 bg-background-200 md:grid-cols-3 xl:grid-cols-4">
              {list.map((r) => (
                <ProductCard key={r.listing.sku} r={r} supplier={supplier} channel={channel} canEdit={canEdit} stockText={stockText} onOpen={setOpen} onRow={onRow} onToast={onToast} />
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === 'prices' && <PricesTable rows={rows} canEdit={canEdit} onRow={onRow} onToast={onToast} supplier={supplier} onOpen={setOpen} />}

      {current && <EditDrawer row={current} types={types} canEdit={canEdit} supplier={supplier} channel={channel} related={rows.filter((r) => r.listing.sku !== current.listing.sku && (r.listing.name.en || '').split(' ')[0] === (current.listing.name.en || '').split(' ')[0]).slice(0, 4)} onClose={() => setOpen(null)} onRow={onRow} onToast={onToast} />}
    </div>
  );
}

// ---------------------------------------------------------------- карточка витрины
/** Карточка как на сайте + (вне режима поставщика) блок для сотрудников: закупка из прайса, рекомендованная цена
 * поставщика, наша цена на сайте с правкой на месте и маржа, которая пересчитывается при вводе (запрос владельца 30.10.2026). */
function ProductCard({ r, supplier, channel, canEdit, stockText, onOpen, onRow, onToast }: { r: ListingRow; supplier: boolean; channel: Channel; canEdit: boolean; stockText: (r: ListingRow) => string; onOpen: (sku: string) => void; onRow: (r: ListingRow) => void; onToast: Props['onToast'] }) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<string | null>(null);
  const img = r.listing.images[0]?.url;
  const b2b = channel === 'b2b';
  const saved = (b2b ? r.prices.b2b : r.prices.b2c) ?? null;
  const manual = b2b ? r.prices.b2bManual : r.prices.b2cManual;
  const calc = b2b ? r.prices.calcB2b : r.prices.calcB2c;
  const live = draft == null ? saved : numOrNull(draft);
  const cost = r.prices.cost;
  const marginAed = live != null && cost != null ? live - cost : null;
  const marginPct = marginAed != null && live ? (marginAed / live) * 100 : null;
  const marginCls = marginPct == null ? 'text-foreground-400' : marginPct < 10 ? 'text-rose-600' : marginPct < 25 ? 'text-accent-700' : 'text-primary-700';

  const commit = async () => {
    if (draft == null) return;
    const val = numOrNull(draft);
    setDraft(null);
    if (val === saved || (val != null && (Number.isNaN(val) || val < 0))) return;
    try {
      onRow(await onboardingApi.patchListing(r.listing.sku, b2b ? { priceB2b: val } : { priceB2c: val }));
    } catch (e) {
      onToast(t('onb.error', { code: (e as ApiError).code }), 'error');
    }
  };
  const stop = (e: React.SyntheticEvent) => e.stopPropagation();

  return (
    <div className="flex flex-col bg-background-50 p-4 text-center transition-colors hover:bg-background-100">
      <button type="button" onClick={() => onOpen(r.listing.sku)} className="flex w-full cursor-pointer flex-col items-center text-center">
        <div className="mb-3 flex aspect-square w-full items-center justify-center overflow-hidden rounded-md bg-white">
          {img ? <img src={img} alt={r.listing.name.en || ''} className="max-h-[85%] max-w-[85%] object-contain" loading="lazy" /> : <span className="text-[11px] text-foreground-400">{t('onb.noImage')}</span>}
        </div>
        <h3 className="text-[13px] font-medium leading-5 text-foreground-950">{r.listing.name.en}</h3>
        <p className="tnum mt-1 text-[15px] font-bold text-accent-700">{t('onb.price', { value: fmtAed(live) })}</p>
        <p className="text-[10.5px] text-foreground-500">
          {t('onb.exclVat')} · <span className="text-accent-700">{stockText(r)}</span>
        </p>
        <span className="mx-auto mt-3 inline-flex h-7 items-center gap-1 rounded-md border border-accent-500 px-3 text-[11px] font-semibold text-accent-700">
          <i className="ri-shopping-cart-2-line" /> {t('onb.addToCart')}
        </span>
      </button>
      <div className="mt-2 flex flex-wrap justify-center gap-1">
        <StatusBadge tone={toneOf(r.listingStatus)} dot={false}>{t(`onb.status.${r.listingStatus}`)}</StatusBadge>
        {r.excludedByPartner && <StatusBadge tone="danger" dot={false}>{t('onb.excluded')}</StatusBadge>}
      </div>
      {!supplier && (
        <dl className="tnum mt-2 grid grid-cols-[auto_1fr] items-center gap-x-2 gap-y-1 rounded border border-dashed border-secondary-300 bg-secondary-50 px-2 py-1.5 text-left text-[10.5px] text-secondary-900" onClick={stop}>
          <dt className="text-secondary-700">{t('onb.cardCost')}</dt>
          <dd className="text-right font-medium">{fmtAed(cost)}</dd>
          <dt className="text-secondary-700">{t('onb.cardRrp')}</dt>
          <dd className="text-right font-medium">{r.prices.rrp != null ? `${fmtAed(r.prices.rrp)} (${fmtPct(r.prices.rrpMarkup)})` : '—'}</dd>
          <dt className="text-secondary-700">{t(b2b ? 'onb.cardOurPriceB2b' : 'onb.cardOurPrice')}</dt>
          <dd className="flex items-center justify-end gap-1">
            <input
              type="number"
              min={0}
              step={1}
              disabled={!canEdit}
              aria-label={t(b2b ? 'onb.cardOurPriceB2b' : 'onb.cardOurPrice')}
              title={manual ? t('onb.cardManualHint', { value: fmtAed(calc) }) : t('onb.cardCalcHint')}
              className={`tnum h-6 w-20 rounded border px-1.5 text-right text-[11px] font-semibold outline-none focus:border-primary-400 disabled:bg-transparent ${manual ? 'border-accent-400 bg-accent-50 text-accent-900' : 'border-secondary-300 bg-background-50 text-foreground-900'}`}
              value={draft ?? (saved ?? '')}
              placeholder={fmtAed(calc)}
              onChange={(e) => setDraft(e.target.value)}
              onBlur={() => void commit()}
              onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); if (e.key === 'Escape') setDraft(null); }}
            />
          </dd>
          <dt className="text-secondary-700">{t('onb.cardMargin')}</dt>
          <dd className={`text-right font-semibold ${marginCls}`}>{marginAed == null ? '—' : `${fmtAed(marginAed)} · ${marginPct!.toFixed(0)} %`}</dd>
        </dl>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- таблица цен
function PricesTable({ rows, canEdit, onRow, onToast, supplier, onOpen }: { rows: ListingRow[]; canEdit: boolean; onRow: (r: ListingRow) => void; onToast: Props['onToast']; supplier: boolean; onOpen: (sku: string) => void }) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<Record<string, string>>({});
  const key = (sku: string, k: string) => `${sku}:${k}`;

  const commit = async (r: ListingRow, k: 'rrp' | 'priceB2c' | 'priceB2b') => {
    const raw = draft[key(r.listing.sku, k)];
    if (raw === undefined) return;
    const cur = k === 'rrp' ? r.prices.rrp : k === 'priceB2c' ? r.prices.b2c : r.prices.b2b;
    const val = numOrNull(raw);
    if (val === cur) return;
    try {
      onRow(await onboardingApi.patchListing(r.listing.sku, { [k]: val }));
    } catch (e) {
      onToast(t('onb.error', { code: (e as ApiError).code }), 'error');
    }
  };
  const cell = (r: ListingRow, k: 'rrp' | 'priceB2c' | 'priceB2b', value: number | null, manual: boolean) => (
    <input
      type="number"
      min={0}
      step={1}
      disabled={!canEdit}
      className={`tnum h-8 w-24 rounded-md border px-2 text-right text-[12px] outline-none focus:border-primary-400 ${manual ? 'border-accent-400 bg-accent-50 text-accent-900' : 'border-background-200 bg-background-50'}`}
      value={draft[key(r.listing.sku, k)] ?? (value ?? '')}
      onChange={(e) => setDraft((d) => ({ ...d, [key(r.listing.sku, k)]: e.target.value }))}
      onBlur={() => void commit(r, k)}
      onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
    />
  );
  const cls = (v: number | null) => (v == null ? 'text-foreground-400' : v < 10 ? 'font-semibold text-rose-600' : v < 25 ? 'font-semibold text-accent-700' : 'text-primary-700');
  const stockVal = (f: (r: ListingRow) => number | null) => rows.reduce((s, r) => s + (f(r) ?? 0) * r.listing.availability.qty, 0);

  return (
    <div className="overflow-x-auto rounded-lg border border-background-200 bg-background-50">
      <div className="border-b border-background-200 px-4 py-3">
        <h2 className="text-sm font-semibold text-foreground-950">{t('onb.pricesTitle')}</h2>
        <p className="mt-0.5 max-w-4xl text-[11.5px] leading-4 text-foreground-500">{t('onb.pricesDesc')}</p>
      </div>
      <table className="w-full min-w-[1100px] text-[12px]">
        <thead>
          <tr className="text-left text-[10.5px] uppercase tracking-[0.06em] text-foreground-500">
            {[t('onb.colName'), t('onb.colSku'), t('onb.colStock'), ...(supplier ? [] : [t('onb.colCost')]), t('onb.colRrp'), ...(supplier ? [] : [t('onb.colRrpMarkup')]), t('onb.colB2c'), ...(supplier ? [] : [t('onb.colB2cMarkup')]), t('onb.colB2cVsRrp'), t('onb.colB2b'), ...(supplier ? [] : [t('onb.colB2bMarkup'), t('onb.colMargin')]), t('onb.colStatus')].map((h) => (
              <th key={h} className="whitespace-nowrap px-3 py-2 font-semibold first:pl-4">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.listing.sku} className="border-t border-background-200">
              <td className="max-w-[260px] px-3 py-1.5 pl-4">
                <button type="button" onClick={() => onOpen(r.listing.sku)} className="flex cursor-pointer items-center gap-2 text-left">
                  {r.listing.images[0] ? <img src={r.listing.images[0].url} alt="" className="h-8 w-8 rounded bg-white object-contain" /> : <span className="h-8 w-8 rounded bg-background-100" />}
                  <span className="truncate text-foreground-900">{r.listing.name.en}</span>
                </button>
              </td>
              <td className="tnum px-3 py-1.5 text-foreground-600">{r.listing.sku}</td>
              <td className="tnum px-3 py-1.5">{r.listing.availability.qty}</td>
              {!supplier && <td className="tnum px-3 py-1.5">{fmtAed(r.prices.cost)}</td>}
              <td className="px-3 py-1.5">{cell(r, 'rrp', r.prices.rrp, false)}</td>
              {!supplier && <td className={`tnum px-3 py-1.5 ${cls(r.prices.rrpMarkup)}`}>{fmtPct(r.prices.rrpMarkup)}</td>}
              <td className="px-3 py-1.5">{cell(r, 'priceB2c', r.prices.b2c, r.prices.b2cManual)}</td>
              {!supplier && <td className={`tnum px-3 py-1.5 ${cls(r.prices.markupB2cActual)}`}>{fmtPct(r.prices.markupB2cActual)}</td>}
              <td className={`tnum px-3 py-1.5 ${r.prices.b2cVsRrp != null && r.prices.b2cVsRrp < 0 ? 'font-semibold text-accent-700' : ''}`}>{fmtPct(r.prices.b2cVsRrp)}</td>
              <td className="px-3 py-1.5">{cell(r, 'priceB2b', r.prices.b2b, r.prices.b2bManual)}</td>
              {!supplier && <td className={`tnum px-3 py-1.5 ${cls(r.prices.markupB2bActual)}`}>{fmtPct(r.prices.markupB2bActual)}</td>}
              {!supplier && <td className="tnum px-3 py-1.5">{fmtAed(r.prices.marginB2c)}</td>}
              <td className="px-3 py-1.5">
                <StatusBadge tone={toneOf(r.listingStatus)} dot={false}>{t(`onb.status.${r.listingStatus}`)}</StatusBadge>
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="border-t border-background-300 bg-background-100 font-semibold">
            <td className="px-3 py-2 pl-4" colSpan={3}>{t('onb.totalsRow', { count: rows.length })}</td>
            {!supplier && <td className="tnum px-3 py-2">{fmtAed(stockVal((r) => r.prices.cost))}</td>}
            <td />
            {!supplier && <td />}
            <td className="tnum px-3 py-2">{fmtAed(stockVal((r) => r.prices.b2c))}</td>
            {!supplier && <td />}
            <td />
            <td className="tnum px-3 py-2">{fmtAed(stockVal((r) => r.prices.b2b))}</td>
            {!supplier && <td colSpan={2} />}
            <td />
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------- карточка товара + правка
function EditDrawer({ row, types, canEdit, supplier, channel, related, onClose, onRow, onToast }: { row: ListingRow; types: ListingTypes | null; canEdit: boolean; supplier: boolean; channel: Channel; related: ListingRow[]; onClose: () => void; onRow: (r: ListingRow) => void; onToast: Props['onToast'] }) {
  const { t } = useTranslation();
  const L = row.listing;
  const [tab, setTab] = useState<'d' | 's' | 'l'>('d');
  const [edit, setEdit] = useState(false);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState(() => ({
    site_type: (types?.types.find((x) => (L.name.en || '').startsWith(x)) || ''), site_category: L.category.primary || '', site_category_extra: L.category.additional.join(', '), name_en: L.name.en || '', short_en: L.short_description.en || '',
    description_en: (L.description_html.en || '').replace(/<ul>.*<\/ul>/s, '').replace(/<\/?p>/g, '').trim(), features_en: L.features.en.join('\n'), specs_en: Object.entries(L.specifications.en).map(([k, v]) => `${k}: ${v}`).join('\n'),
    group_key: L.group?.key || '', variant_label: L.group?.variant_label.en || '', seo_title: L.seo.title.en || '', seo_description: L.seo.description.en || '', delivery_days_min: String(L.logistics.delivery_days_min ?? ''), delivery_days_max: String(L.logistics.delivery_days_max ?? ''), warranty_months: String(L.logistics.warranty_months ?? ''),
    rrp: row.prices.rrp == null ? '' : String(row.prices.rrp), priceB2c: row.prices.b2cManual && row.prices.b2c != null ? String(row.prices.b2c) : '', priceB2b: row.prices.b2bManual && row.prices.b2b != null ? String(row.prices.b2b) : '', siteActionOverride: row.siteActionOverride || '',
  }));
  const s = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => setF((x) => ({ ...x, [k]: e.target.value }));
  const price = channel === 'b2b' ? row.prices.b2b : row.prices.b2c;

  const save = async () => {
    setBusy(true);
    try {
      const specs: Record<string, string> = {};
      f.specs_en.split('\n').forEach((line) => {
        const i = line.indexOf(':');
        if (i > 0) specs[line.slice(0, i).trim()] = line.slice(i + 1).trim();
      });
      const r = await onboardingApi.patchListing(row.listing.sku, {
        listing: {
          site_type: f.site_type || undefined, site_category: f.site_category || null, site_category_extra: f.site_category_extra.split(',').map((x) => x.trim()).filter(Boolean), name_en: f.name_en, short_en: f.short_en, description_en: f.description_en,
          features_en: f.features_en.split('\n').map((x) => x.trim()).filter(Boolean), specs_en: specs, group_key: f.group_key || null, variant_label: f.variant_label || null, seo_title: f.seo_title || null, seo_description: f.seo_description || null,
          delivery_days_min: numOrNull(f.delivery_days_min), delivery_days_max: numOrNull(f.delivery_days_max), warranty_months: numOrNull(f.warranty_months),
        },
        rrp: numOrNull(f.rrp), priceB2c: numOrNull(f.priceB2c), priceB2b: numOrNull(f.priceB2b), siteActionOverride: (f.siteActionOverride || null) as 'show' | 'preorder' | 'hide' | null,
      });
      onRow(r);
      setEdit(false);
      onToast(t('onb.saved'));
    } catch (e) {
      onToast(t('onb.error', { code: (e as ApiError).code }), 'error');
    } finally {
      setBusy(false);
    }
  };
  const upload = async (file: File | undefined) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      onRow(await onboardingApi.uploadImage(row.listing.sku, fd));
    } catch (e) {
      onToast(t('onb.error', { code: (e as ApiError).code }), 'error');
    }
  };
  const makePrimary = async (id: string) => onRow(await onboardingApi.patchListing(row.listing.sku, { images: row.images.map((i) => ({ id: i.id, is_primary: i.id === id })) }));
  const remove = async (id: string) => onRow(await onboardingApi.deleteImage(row.listing.sku, id));

  return (
    <div className="fixed inset-0 z-40 flex justify-end bg-foreground-950/30" onClick={onClose}>
      <div className="h-full w-full max-w-4xl overflow-y-auto bg-background-50 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-background-200 bg-background-50 px-5 py-3">
          <div className="min-w-0">
            <p className="text-[10.5px] font-semibold uppercase tracking-[0.14em] text-foreground-500">{f.site_type || '—'}</p>
            <h2 className="truncate text-[15px] font-semibold text-foreground-950">{L.name.en}</h2>
          </div>
          <div className="flex items-center gap-2">
            {canEdit && !edit && (
              <button type="button" onClick={() => setEdit(true)} className={btnPrimary}>
                <i className="ri-edit-line text-[13px]" /> {t('onb.editTitle')}
              </button>
            )}
            <button type="button" onClick={onClose} aria-label="close" className="cursor-pointer text-foreground-400 hover:text-foreground-800">
              <i className="ri-close-line text-xl" />
            </button>
          </div>
        </div>

        {!edit ? (
          <div className="p-5">
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div>
                <div className="flex aspect-square items-center justify-center overflow-hidden rounded-md bg-white">
                  {L.images[0] ? <img src={L.images[0].url} alt="" className="max-h-[80%] max-w-[80%] object-contain" /> : <span className="text-[11px] text-foreground-400">{t('onb.noImage')}</span>}
                </div>
                <div className="mt-2 flex gap-2 overflow-x-auto">
                  {L.images.map((i) => (
                    <img key={i.image_id} src={i.url} alt="" className="h-14 w-14 rounded border border-background-200 bg-white object-contain" />
                  ))}
                </div>
              </div>
              <div>
                <div className="text-[11.5px] text-foreground-500">
                  SKU: {L.sku} <span className="mx-1 text-background-400">|</span> <span className="text-accent-700">{L.availability.qty > 0 ? `In Stock: ${L.availability.qty} pcs` : L.availability.state === 'in_transit' ? `In transit: ${L.availability.in_transit?.qty}` : 'On request'}</span>
                </div>
                {L.color && <p className="mt-3 text-[12.5px] text-foreground-600">Colour: <b className="text-foreground-900">{L.color}</b></p>}
                <div className="mt-4 rounded-md border border-background-300 bg-background-100 p-4">
                  <p className="tnum text-[24px] font-bold text-accent-700">{channel === 'b2b' ? 'Dealer price' : 'Price'}: AED {fmtAed(price, 2)}</p>
                  <p className="text-[11px] text-foreground-500">{t('onb.exclVat')}</p>
                </div>
                <span className="mt-3 inline-flex h-10 w-full items-center justify-center rounded-md bg-accent-500 text-[13px] font-bold text-white">{t('onb.addToCart')}</span>
                {!supplier && (
                  <p className="tnum mt-3 rounded border border-dashed border-secondary-300 bg-secondary-50 px-2 py-1.5 text-[11px] text-secondary-900">
                    {t('onb.cardAnno', { cost: fmtAed(row.prices.cost), b2c: fmtAed(row.prices.b2c), mB2c: fmtPct(row.prices.markupB2cActual), b2b: fmtAed(row.prices.b2b), mB2b: fmtPct(row.prices.markupB2bActual) })}
                    {row.prices.rrp != null ? ` · ${t('onb.rrp')} ${fmtAed(row.prices.rrp)} (${fmtPct(row.prices.rrpMarkup)}) · B2C/RRP ${fmtPct(row.prices.b2cVsRrp)}` : ''}
                  </p>
                )}
                <div className="mt-4 flex gap-5 text-[11.5px] text-foreground-600">
                  <span><b className="block text-foreground-900">{t('onb.deliveryBadge', { min: L.logistics.delivery_days_min ?? '—', max: L.logistics.delivery_days_max ?? '—' })}</b>Fast delivery</span>
                  <span><b className="block text-foreground-900">{t('onb.warrantyBadge', { months: L.logistics.warranty_months ?? '—' })}</b>Full coverage</span>
                </div>
                {row.problems.length > 0 && (
                  <div className="mt-4 flex flex-wrap gap-1">
                    {row.problems.map((p) => (
                      <StatusBadge key={p} tone="danger" dot={false}>{t(`onb.problems.${p}`)}</StatusBadge>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="mt-6 flex gap-6 border-b border-background-200 text-[13px]">
              {(['d', 's', 'l'] as const).map((k) => (
                <button key={k} type="button" onClick={() => setTab(k)} className={`-mb-px cursor-pointer border-b-2 pb-2 font-medium ${tab === k ? 'border-accent-500 text-foreground-950' : 'border-transparent text-foreground-500'}`}>
                  {t(k === 'd' ? 'onb.detailDescription' : k === 's' ? 'onb.detailSpecs' : 'onb.detailDelivery')}
                </button>
              ))}
            </div>
            <div className="prose-sm max-w-[72ch] pt-4 text-[13px] leading-6 text-foreground-700">
              {tab === 'd' && (
                <>
                  {L.short_description.en && <p>{L.short_description.en}</p>}
                  <div dangerouslySetInnerHTML={{ __html: L.description_html.en || '' }} />
                </>
              )}
              {tab === 's' && (
                <table className="w-full max-w-[560px] text-[12.5px]">
                  <tbody>
                    {Object.entries(L.specifications.en).map(([k, v]) => (
                      <tr key={k} className="border-b border-background-200">
                        <td className="w-[44%] py-1.5 text-foreground-500">{k}</td>
                        <td className="py-1.5">{v}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {tab === 'l' && <p>{t('onb.deliveryText', { min: L.logistics.delivery_days_min ?? '—', max: L.logistics.delivery_days_max ?? '—' })}</p>}
            </div>
            {related.length > 0 && (
              <div className="mt-6">
                <h3 className="mb-2 text-[13px] font-semibold text-foreground-950">{t('onb.related')}</h3>
                <div className="grid grid-cols-2 gap-px rounded-lg border border-background-200 bg-background-200 md:grid-cols-4">
                  {related.map((r) => (
                    <div key={r.listing.sku} className="bg-background-50 p-3 text-center text-[11.5px]">
                      <div className="mb-2 flex aspect-square items-center justify-center rounded bg-white">{r.listing.images[0] && <img src={r.listing.images[0].url} alt="" className="max-h-[80%] max-w-[80%] object-contain" />}</div>
                      <p className="text-foreground-900">{r.listing.name.en}</p>
                      <p className="tnum font-bold text-accent-700">AED {fmtAed(channel === 'b2b' ? r.prices.b2b : r.prices.b2c)}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 p-5 md:grid-cols-2">
            <Field label={t('onb.fType')}>
              <select className={inputCls} value={f.site_type} onChange={(e) => { const ty = e.target.value; const m = types?.map.find((x) => x.hubType === ty); setF((x) => ({ ...x, site_type: ty, site_category: m?.primary || x.site_category, site_category_extra: m ? m.additional.join(', ') : x.site_category_extra })); }}>
                <option value="">—</option>
                {types?.types.map((x) => <option key={x} value={x}>{x}</option>)}
              </select>
            </Field>
            <Field label={t('onb.fCategory')}><input className={inputCls} value={f.site_category} onChange={s('site_category')} placeholder="executive-chair" /></Field>
            <Field label={t('onb.fName')} className="md:col-span-2"><input className={inputCls} value={f.name_en} onChange={s('name_en')} /></Field>
            <Field label={t('onb.fShort')} className="md:col-span-2"><input className={inputCls} value={f.short_en} onChange={s('short_en')} /></Field>
            <Field label={t('onb.fDescription')} className="md:col-span-2"><textarea className={areaCls} rows={3} value={f.description_en} onChange={s('description_en')} /></Field>
            <Field label={t('onb.fFeatures')}><textarea className={areaCls} rows={6} value={f.features_en} onChange={s('features_en')} /></Field>
            <Field label={t('onb.fSpecs')}><textarea className={areaCls} rows={6} value={f.specs_en} onChange={s('specs_en')} /></Field>
            <Field label={t('onb.fCategoryExtra')}><input className={inputCls} value={f.site_category_extra} onChange={s('site_category_extra')} /></Field>
            <Field label={t('onb.fGroup')}><input className={inputCls} value={f.group_key} onChange={s('group_key')} /></Field>
            <Field label={t('onb.fVariantLabel')}><input className={inputCls} value={f.variant_label} onChange={s('variant_label')} /></Field>
            <Field label={t('onb.fWarranty')}><input className={`${inputCls} tnum`} type="number" value={f.warranty_months} onChange={s('warranty_months')} /></Field>
            <Field label={t('onb.fDelivery')}>
              <div className="flex items-center gap-2">
                <input className={`${inputCls} tnum`} type="number" value={f.delivery_days_min} onChange={s('delivery_days_min')} />
                <span>–</span>
                <input className={`${inputCls} tnum`} type="number" value={f.delivery_days_max} onChange={s('delivery_days_max')} />
              </div>
            </Field>
            <Field label={t('onb.fSiteAction')}>
              <select className={inputCls} value={f.siteActionOverride} onChange={s('siteActionOverride')}>
                {(['', 'show', 'preorder', 'hide'] as const).map((x) => <option key={x} value={x}>{t(`onb.siteAction.${x || 'auto'}`)}</option>)}
              </select>
            </Field>
            <Field label={t('onb.fSeoTitle')}><input className={inputCls} value={f.seo_title} onChange={s('seo_title')} /></Field>
            <Field label={t('onb.fSeoDescription')}><input className={inputCls} value={f.seo_description} onChange={s('seo_description')} /></Field>
            {!supplier && (
              <>
                <Field label={t('onb.fRrp')}><input className={`${inputCls} tnum`} type="number" value={f.rrp} onChange={s('rrp')} /></Field>
                <Field label={t('onb.fPriceB2c')} hint={t('onb.calc', { value: fmtAed(row.prices.calcB2c) })}><input className={`${inputCls} tnum`} type="number" value={f.priceB2c} onChange={s('priceB2c')} placeholder={fmtAed(row.prices.calcB2c)} /></Field>
                <Field label={t('onb.fPriceB2b')} hint={t('onb.calc', { value: fmtAed(row.prices.calcB2b) })}><input className={`${inputCls} tnum`} type="number" value={f.priceB2b} onChange={s('priceB2b')} placeholder={fmtAed(row.prices.calcB2b)} /></Field>
              </>
            )}
            <div className="md:col-span-2">
              <p className="mb-1 text-[11px] font-medium text-foreground-600">{t('onb.fImages')}</p>
              <div className="flex flex-wrap gap-3">
                {row.images.map((i) => (
                  <div key={i.id} className={`w-28 rounded-md border p-1.5 text-center text-[10.5px] ${i.is_primary ? 'border-primary-400' : 'border-background-200'}`}>
                    <img src={`${L.images.find((x) => x.image_id === i.id)?.url || ''}`} alt="" className="mx-auto h-20 w-full rounded bg-white object-contain" />
                    <div className="mt-1 flex justify-between">
                      {i.is_primary ? <span className="font-semibold text-primary-700">{t('onb.fPrimary')}</span> : <button type="button" className="cursor-pointer text-primary-700" onClick={() => void makePrimary(i.id)}>{t('onb.fMakePrimary')}</button>}
                      <button type="button" className="cursor-pointer text-rose-600" onClick={() => void remove(i.id)}>{t('onb.fRemove')}</button>
                    </div>
                  </div>
                ))}
                <label className={`${btnGhost} h-auto flex-col px-4 py-5`}>
                  <i className="ri-image-add-line text-lg" />
                  {t('onb.fAddImage')}
                  <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => void upload(e.target.files?.[0])} />
                </label>
              </div>
            </div>
            <div className="flex justify-end gap-2 md:col-span-2">
              <button type="button" onClick={() => setEdit(false)} className={btnGhost}>{t('onb.cancel')}</button>
              <button type="button" onClick={save} disabled={busy} className={btnPrimary}><i className="ri-save-3-line text-[13px]" /> {busy ? t('onb.saving') : t('onb.save')}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
